/**
 * Client for the KS RAG /chat, mirroring qceService's plain token-bound shape.
 *
 * Two legs:
 *  1) getChatToken — asks the CMS (node) to mint a short-lived, project-scoped
 *     rag_api JWT and hand back the KS chat URL. The CMS holds the private key;
 *     the browser never mints tokens.
 *  2) streamChat — opens an SSE stream POST directly to the KS chat URL with that
 *     bearer. We go direct (not through the CMS) because Heroku caps requests at
 *     29s, which would kill a streamed answer. Events arrive as
 *     status → citations → text(delta)* → done (or error).
 */
import axios from "axios";

// Matches the portal (theirstory-portal-platform/types/chat.ts) Citation exactly
// — the KS is the single source of truth and emits these names, so the ported
// portal components read them without an adapter.
export interface Citation {
  index: number; // 1-based; matches the [n] markers in the answer text
  transcription: string; // the quoted text / chapter summary
  text?: string;
  speaker: string;
  interviewTitle: string;
  storyTitle?: string;
  sectionTitle: string;
  chapterTitle?: string;
  startTime: number; // seconds
  endTime: number;
  theirstoryId: string; // source story id
  storyId?: string;
  thumbnailUrl?: string; // Mux thumbnail (CMS-enriched) — grouping + beauty, no playback
  isAudioFile?: boolean;
  score?: number;
  isChapterSynopsis?: boolean; // chapter summary vs verbatim clip (chip color/label)
}

// Per-story display metadata the CMS owns (title, thumbnail, audio flag). Merged
// into citations after they arrive so titles/thumbnails stay current — see
// chatSlice.mergeCitationMeta.
export interface CitationMeta {
  title: string;
  thumbnailUrl?: string;
  isAudioFile: boolean;
}

export async function getCitationMeta(projectId: string, storyIds: string[]): Promise<Record<string, CitationMeta>> {
  const { data } = await axios.post<Record<string, CitationMeta>>(`/projects/${projectId}/chat-citation-meta`, {
    storyIds,
  });
  return data;
}

export type ChatStreamEvent =
  | { type: "conversation"; conversationId: string }
  | { type: "status"; message: string }
  | { type: "citations"; citations: Citation[] }
  | { type: "text"; delta: string }
  | { type: "done" }
  | { type: "error"; message: string };

interface ChatTokenResponse {
  token: string;
  chatUrl: string;
}

// Mint the rag_api token + resolve the KS chat URL (CMS session auth is already
// on the shared axios instance).
export async function getChatToken(orgId: string, projectId: string): Promise<ChatTokenResponse> {
  const { data } = await axios.post<ChatTokenResponse>("/rag-chat-token", { orgId, projectId });
  return data;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

// Project vectorization gate: RAG chat is only reliable once every story in the
// project is indexed in Weaviate.
export interface ProjectVectorStatus {
  total: number; // interviews with a transcript (indexable)
  interviewsTotal: number; // all interviews in the project (what the card shows)
  indexed: number; // actually present in Weaviate (ground-truth "done")
  inFlight: number; // claimed/processing, not yet indexed (Indexing…)
  remaining: number; // ingestable but not yet in Weaviate
  failed: number; // advisory: last attempt errored (subset of remaining)
  ready: boolean;
  knowledgeServiceEnabled: boolean;
}

export async function getProjectVectorStatus(projectId: string): Promise<ProjectVectorStatus> {
  const { data } = await axios.get<ProjectVectorStatus>(`/projects/${projectId}/weaviate-status`);
  return data;
}

// Trigger ingestion for every not-yet-indexed story in the project (202, async).
export async function ensureProjectVectors(projectId: string): Promise<{ triggered: number; total: number }> {
  const { data } = await axios.post<{ triggered: number; total: number }>(`/projects/${projectId}/weaviate-ensure`, {});
  return data;
}

// Normalize one SSE payload into a ChatStreamEvent. Accepts either a named-event
// frame (mapped via `evName`) or a `data:` JSON object carrying its own `type`.
function toEvent(evName: string | undefined, raw: string): ChatStreamEvent | null {
  let payload: any = raw;
  try {
    payload = JSON.parse(raw);
  } catch {
    /* plain-text delta or keep-alive */
  }
  const type = evName || payload?.type;
  switch (type) {
    case "conversation":
      return { type: "conversation", conversationId: String(payload?.conversationId ?? "") };
    case "status": {
      const msg =
        typeof payload === "string"
          ? payload
          : typeof payload?.message === "string"
          ? payload.message
          : typeof payload?.status === "string"
          ? payload.status
          : typeof payload?.step === "string"
          ? payload.step
          : "";
      return { type: "status", message: msg || "Thinking…" };
    }
    case "citations":
      return {
        type: "citations",
        citations: (payload?.citations ?? []).map((c: any) => ({
          index: c.index,
          transcription: c.text ?? "",
          text: c.text ?? "",
          // Empty (never "Unknown") — display sites fall back to the story title.
          speaker: c.speaker || (c.isChapterSynopsis ? "Chapter Summary" : ""),
          interviewTitle: c.storyTitle ?? "",
          storyTitle: c.storyTitle ?? "",
          sectionTitle: c.chapterTitle ?? "",
          chapterTitle: c.chapterTitle ?? "",
          startTime: c.startTime,
          endTime: c.endTime,
          theirstoryId: c.storyId,
          storyId: c.storyId,
          score: c.score,
          isChapterSynopsis: Boolean(c.isChapterSynopsis),
        })),
      };
    case "text":
    case "delta":
      return { type: "text", delta: String(payload?.content ?? payload?.delta ?? payload?.text ?? payload ?? "") };
    case "done":
      return { type: "done" };
    case "error":
      return { type: "error", message: String(payload?.message ?? "Chat failed") };
    default:
      return null;
  }
}

// Stream a chat turn. Calls onEvent for each parsed event; resolves when the
// stream closes. Pass an AbortSignal to cancel an in-flight answer.
export async function streamChat(
  args: {
    chatUrl: string;
    token: string;
    message: string;
    history: ChatMessage[];
    orgId: string;
    projectId: string;
    // Echo the id from the KS `conversation` event to continue a thread
    // (server-side history + prompt-cache reuse); omit on the first turn.
    conversationId?: string;
  },
  onEvent: (e: ChatStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(args.chatUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${args.token}`,
    },
    body: JSON.stringify({
      // KS contract: top-level `query` is the question; `messages` is the prior
      // history (optional). conversationId, when present, must be a string.
      query: args.message,
      messages: args.history,
      orgId: args.orgId,
      projectId: args.projectId,
      ...(args.conversationId ? { conversationId: args.conversationId } : {}),
    }),
    signal,
  });

  if (!res.ok || !res.body) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.error || body?.detail || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Chat request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // Parse SSE frames separated by a blank line. Each frame may have `event:` and
  // one or more `data:` lines.
  const flushFrame = (frame: string) => {
    let evName: string | undefined;
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) evName = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
    }
    if (dataLines.length === 0) return;
    const data = dataLines.join("\n");
    if (data === "[DONE]") {
      onEvent({ type: "done" });
      return;
    }
    const ev = toEvent(evName, data);
    if (ev) onEvent(ev);
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep = buffer.indexOf("\n\n");
    // Frames are delimited by a blank line (\n\n).
    while (sep !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      flushFrame(frame);
      sep = buffer.indexOf("\n\n");
    }
  }
  if (buffer.trim()) flushFrame(buffer);
}
